import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import AdminTabs from '../AdminTabs';
import {
  AdminHead, AdminPage, Banner, Button, ConfirmDialog, SkeletonRows,
} from '../kit';
import { apiErrorMessage, fetchOrganizationsSummary } from '../../../utils/organizationApi';
import { orgInitial } from './orgModel';
import { deriveRefreshState } from './refreshModel';
import OrgSettingsTab from './OrgSettingsTab';
import OrgLocationsTab from './OrgLocationsTab';
import OrgRefreshTab from './OrgRefreshTab';
import './orgs.css';

const TABS = ['settings', 'locations', 'refresh'];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function OrganizationDetailPage() {
  const { orgId: orgIdParam } = useParams();
  const orgId = Number.parseInt(orgIdParam, 10);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'settings';

  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [actions, setActions] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [pendingTab, setPendingTab] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!Number.isInteger(orgId)) { setNotFound(true); setLoading(false); return; }
    if (!silent) setLoading(true);
    try {
      const res = await fetchOrganizationsSummary({ orgId });
      const found = (res?.organizations || []).find((o) => Number(o.id) === orgId) || null;
      setOrg(found);
      setNotFound(!found);
      setError(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) setNotFound(true);
      else if (!silent) setError(apiErrorMessage(err, 'Could not load this organization.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);
  // Each tab publishes its head actions / dirty flag and clears them on unmount.

  const refreshState = useMemo(
    () => (org ? deriveRefreshState(org, { liveStatus }) : null),
    [org, liveStatus]
  );
  const failed = refreshState && (refreshState.key === 'failed' || refreshState.key === 'partial');

  const goTab = (key) => {
    if (key === tab) return;
    if (dirty) { setPendingTab(key); return; }
    applyTab(key);
  };
  const applyTab = (key) => {
    if (key === 'users') { navigate(`/admin/users?org=${orgId}`); return; }
    setSearchParams(key === 'settings' ? {} : { tab: key }, { replace: false });
  };

  const back = () => navigate('/admin/organizations');

  if (notFound && !loading) {
    return (
      <AdminPage>
        <AdminTabs />
        <AdminHead title="Organization not found" crumb={{ label: 'Organizations', onClick: back }} />
        <div className="ak-frame">
          <div className="ak-empty">
            <strong>We couldn't find organization #{orgIdParam}</strong>
            It may have been deleted, or you may not have access to it.
            <div style={{ marginTop: 12 }}><Button onClick={back}>Back to organizations</Button></div>
          </div>
        </div>
      </AdminPage>
    );
  }

  const locationCount = org?.locationCount ?? null;
  const userCount = org?.userCount ?? null;
  const tabs = [
    { key: 'settings', label: 'Settings' },
    { key: 'locations', label: 'Locations', count: locationCount ?? undefined },
    { key: 'refresh', label: 'Data refresh', dot: Boolean(failed) },
    { key: 'users', label: 'Users', count: userCount ?? undefined },
  ];

  const subtitle = org ? (
    <span className="orgs-headline">
      {[org.code, `#${org.id}`, locationCount !== null ? plural(locationCount, 'location') : null, userCount !== null ? plural(userCount, 'user') : null]
        .filter(Boolean).join(' · ')}
    </span>
  ) : null;

  return (
    <AdminPage>
      <AdminTabs />
      <AdminHead
        crumb={{ label: 'Organizations', onClick: back }}
        mark={org ? orgInitial(org) : null}
        title={org ? org.name : (loading ? 'Loading…' : 'Organization')}
        subtitle={subtitle}
        tabs={org ? tabs : null}
        activeTab={tab}
        onTabChange={goTab}
        actions={org ? actions : null}
      />

      {error ? (
        <Banner tone="bad" icon={AlertTriangle} title="Couldn't load this organization" action={<Button onClick={() => load()}>Try again</Button>}>
          {error}
        </Banner>
      ) : null}

      {loading && !org ? (
        <div className="ak-frame" aria-busy="true">
          <table className="ak-tbl"><tbody><SkeletonRows rows={5} columns={4} /></tbody></table>
        </div>
      ) : null}

      {org && tab === 'settings' ? (
        <OrgSettingsTab
          key={`settings-${org.id}`}
          org={org}
          setActions={setActions}
          onDirtyChange={setDirty}
          onSaved={() => load({ silent: true })}
          onDeleted={back}
        />
      ) : null}
      {org && tab === 'locations' ? (
        <OrgLocationsTab
          key={`locations-${org.id}`}
          org={org}
          setActions={setActions}
          onChanged={() => load({ silent: true })}
        />
      ) : null}
      {org && tab === 'refresh' ? (
        <OrgRefreshTab
          key={`refresh-${org.id}`}
          org={org}
          refreshState={refreshState}
          onLiveStatus={setLiveStatus}
          onChanged={() => load({ silent: true })}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingTab)}
        title="Discard unsaved changes?"
        confirmLabel="Discard"
        danger
        onCancel={() => setPendingTab(null)}
        onConfirm={() => { const key = pendingTab; setPendingTab(null); setDirty(false); applyTab(key); }}
      >
        <p style={{ margin: 0 }}>You have unsaved changes on Settings. Leaving this tab discards them.</p>
      </ConfirmDialog>
    </AdminPage>
  );
}
